import { fetchApi } from '../utils/apiUtils';

const API_BASE_URL = '/api/admin'; // Base URL for admin routes

// Use centralized API utility with global token expiration handling
async function fetchAdminApi<T>(endpoint: string, token: string | null, options: RequestInit = {}): Promise<T> {
  if (!token) {
    console.warn('No auth token provided to fetchAdminApi for endpoint:', endpoint);
    throw new Error('Authentication token is required. Please log in again.');
  }

  // Add the token to the headers explicitly
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`
  };

  // Use the centralized fetchApi which has global token expiration handling
  return fetchApi<T>(`${API_BASE_URL}${endpoint}`, { ...options, headers }, false);
}

export interface User {
  user_id: number;
  username: string;
  email: string;
  created_at: string;
  approval_status: 'pending_approval' | 'approved' | 'rejected';
  is_admin: boolean | number; // DB might store as 0/1
  is_read_only: boolean | number; // DB might store as 0/1
  last_login: string | null;
}

interface AdminActionResponse {
  message: string;
}

// Get users, optionally filter by status
export const getUsers = async (token: string | null, status?: string): Promise<User[]> => {
  let endpoint = '/users';
  if (status) {
    endpoint += `?status=${status}`;
  }
  return fetchAdminApi<User[]>(endpoint, token, {
    method: 'GET',
  });
};

// Update a user's approval status
export const updateUserStatus = async (userId: number, status: 'pending_approval' | 'approved' | 'rejected', token: string | null): Promise<AdminActionResponse> => {
  return fetchAdminApi<AdminActionResponse>(`/users/${userId}/status`, token, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
};

// Update a user's admin role
export const updateUserAdminRole = async (userId: number, isAdmin: boolean, token: string | null): Promise<AdminActionResponse> => {
  return fetchAdminApi<AdminActionResponse>(`/users/${userId}/admin-role`, token, {
    method: 'PUT',
    body: JSON.stringify({ isAdmin }),
  });
};

// Update a user's read-only role
export const updateUserReadOnlyRole = async (userId: number, isReadOnly: boolean, token: string | null): Promise<AdminActionResponse> => {
  return fetchAdminApi<AdminActionResponse>(`/users/${userId}/read-only`, token, {
    method: 'PUT',
    body: JSON.stringify({ isReadOnly }),
  });
};

// Generate a password reset link for a user (admin only, no email sent)
export const generateResetLink = async (userId: number, token: string | null): Promise<{ resetLink: string }> => {
  return fetchAdminApi<{ resetLink: string }>(`/users/${userId}/reset-link`, token, {
    method: 'POST',
  });
};

export interface ReportRecipient {
  id: number;
  email: string;
  name: string | null;
  is_active: number;
  receives_weekly: number;
  receives_monthly: number;
  created_at: string;
  updated_at: string;
}

export interface ReportRecipientInput {
  email: string;
  name?: string | null;
  is_active?: boolean;
  receives_weekly?: boolean;
  receives_monthly?: boolean;
}

async function fetchRecipientsApi<T>(endpoint: string, token: string | null, options: RequestInit = {}): Promise<T> {
  if (!token) {
    throw new Error('Authentication token is required. Please log in again.');
  }
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  return fetchApi<T>(`/api/report-recipients${endpoint}`, { ...options, headers }, false);
}

export const getReportRecipients = (token: string | null) =>
  fetchRecipientsApi<ReportRecipient[]>('', token, { method: 'GET' });

export const createReportRecipient = (data: ReportRecipientInput, token: string | null) =>
  fetchRecipientsApi<ReportRecipient>('', token, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const updateReportRecipient = (id: number, data: Partial<ReportRecipientInput>, token: string | null) =>
  fetchRecipientsApi<ReportRecipient>(`/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteReportRecipient = (id: number, token: string | null) =>
  fetchRecipientsApi<{ message: string }>(`/${id}`, token, { method: 'DELETE' });

